import { PADDING, POSITION, SHADOW } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { BOOTSTRAP_DEFAULT_BACKDROP_MODAL_Z_INDEX, BOOTSTRAP_DEFAULT_MODAL_Z_INDEX, HtmlElementOptions, KeyboardEventType, KeyboardInput, REF_ATTRIBUTE, createHtmlElement, findElementByRef, getAllParents, getElementByRef, ignoreResizeObserverLoopLimit, setHtmlElementOptions } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import { Instance, Placement, StrictModifiers, VirtualElement, computeStyles, createPopper, eventListeners, flip, hide, preventOverflow } from "@popperjs/core";
import { OffsetsFunction } from "@popperjs/core/lib/modifiers/offset";
import { CjsDialog, DialogOptions, DialogPosition } from "../dialog/dialog";

/** Modes of remove popup */
export type ShowPopupRemoveMode = "close" | "cancel";

/**
 * Mode of showPopup
 * default: Valid with 'click outer showPopup' or 'enter key'
 * direct: Valid with 'first click selection', popup close
 */
export type PopupValidationMode = "default" | "direct";

export type ShowPopupStyling = Pick<HtmlElementOptions, "cssClass" | "style" | "attributes">;

export const defaultCssPopup: ShowPopupStyling = {
    style: {
        backgroundColor: "var(--bs-body-bg)"
    },
    cssClass: [SHADOW.S6]
};

/**
 * Absolute position (top + left) for a popup
 */
export interface AbsolutePosition {
    /** Top position (in px) */
    top: number;
    /** Left position (in px) */
    left: number;
}

export enum PopupPosition {
    AUTO = "auto",
    AUTO_START = "auto-start",
    AUTO_END = "auto-end",
    TOP = "top",
    TOP_START = "top-start",
    TOP_END = "top-end",
    BOTTOM = "bottom",
    BOTTOM_START = "bottom-start",
    BOTTOM_END = "bottom-end",
    RIGHT = "right",
    RIGHT_START = "right-start",
    RIGHT_END = "right-end",
    LEFT = "left",
    LEFT_START = "left-start",
    LEFT_END = "left-end"
}

export interface ShowPopupParams {

    /** HTMLElement that will be append inside the popup */
    elementToShow: HTMLElement;

    /** Parent which popupElement will be attached */
    parent: HTMLElement;

    /** Event that call this function (use to prevent to use stopPropagation) */
    event: Event | undefined;

    /**
     * It is possible to add custom modifiers, written by you, by defining them in the options.modifiers array during a Popper instantiation.
     * @see https://popper.js.org/docs/v2/modifiers/
     */
    modifiers?: StrictModifiers[];

    /**
     * Fixed Position of the popup in the DOM
     */
    absolutePosition?: AbsolutePosition;

    /**
     * The offset modifier lets you displace a popper element from its reference element.
     * This can be useful if you need to apply some margin between them or if you need to 
     * fine tune the position according to some custom logic.
     */
    offset?: OffsetsFunction | [number | null | undefined, number | null | undefined];

    /** Function which will be called when the popupElement will be close */
    onPopupHide?: () => void;

    /** Function which will be called when the popupElement will be cancel */
    onPopupCancel?: () => void;

    /** Css of the popup HTMLElement */
    cssPopup?: ShowPopupStyling;

    /** Position of the popup when it open */
    popupPosition?: PopupPosition;

    /** Disable popup open in an other Dialog if parent is already in a Dialog */
    disablePopupInDialog?: boolean;

    /** 
     * Options of popup in dialog used if popup will be opened in dialog 
     * If an option isn't given, popupInDialog will use same options as parent showPopupParams
     */
    popupInDialogOptions?: Partial<ShowPopupParams & { dialogOptions: Partial<DialogOptions> }>;

    /**
     * Validation mode of popup (default | direct)
     * direct = First pick in popup => close popup 
     */
    popupMode?: PopupValidationMode;

    /**
     * Setting true prevents the removePopup("cancel") on Escape key pressed
     */
    doNotCancelOnEscapeKey?: boolean;

    /**
     * Setting true prevents the removePopup("close") on Enter key pressed
     */
    doNotCloseOnEnterKey?: boolean;

    /** 
     * If false popup will not be closed if user click outer popup bounds 
     * @default true
     */
    popupCanBeCloseOnOuterBoundsClick?: boolean | ((event: Event) => boolean);
}

/** Object returned by ShowPopup */
export interface ShowPopupReturn {

    /** the HTMLElement created */
    popup: HTMLElement;

    /** function that close/cancel the popup and remove it from DOM */
    removePopup: (mode: ShowPopupRemoveMode) => void;

    /** The popper instance */
    popper?: Instance;
}

export interface InstanceWithNewPopupPosition {

    /** Instance to manipulate */
    popper: Instance;

    /** Parameters of position */
    newPosition?: {
        absolutePosition?: AbsolutePosition,
        popupPosition?: Placement
    };

    /** Parent of the popup */
    parent: HTMLElement;
}


/**
 * Create a div with ref=popupElement, add inside an HTMLElement and attach the popupElement to the parent HTMLElement
 * Add a listener which close the popup if we click outside of it or press the key Enter
 * Add a listener which cancel the popup  if we press the key Esc
 * Define a function removePopup with an option mode = "close" or "cancel". This function remove all previous listeners explained then call the optional function onPopupHide/onPopupCancel based on the mode selected
 * @see https://popper.js.org/docs/v2/ for options docs
 */
export function showPopup(p: ShowPopupParams): ShowPopupReturn {

    // Handle parent of popupElement already in dialog, open it in a Dialog instead if required
    if (!p.disablePopupInDialog && isInModal(p.parent)) {
        return handlePopupInModal({
            ...p,
            ...p.popupInDialogOptions
        });
    }

    // If parent is document.body, append popup/tooltip inside popupContainer dedicate to it
    const parent = p.parent === document.body ? getDocumentBodyPopupContainer() : p.parent;

    // Creation of the popupElement
    const popupElement = createHtmlElement("div", {
        attributes: { [REF_ATTRIBUTE]: "popupElement" }
    });

    // Apply css to popupElement
    applyCssStyling(popupElement, p.cssPopup);

    parent.append(popupElement);
    popupElement.append(p.elementToShow);

    // Modifier for offset attribute
    const offsetModifier = {
        name: "offset",
        options: {
            offset: p.offset ?? [0, 0]
        }
    };

    let popper: Instance | undefined;
    if (!p.absolutePosition) {
        popper = createPopper(parent, popupElement, {
            modifiers: [
                preventOverflow,
                flip,
                hide,
                eventListeners,
                computeStyles,
                offsetModifier,
                ...p?.modifiers ?? []
            ],
            placement: p.popupPosition as Placement ?? PopupPosition.AUTO
        });
    } else {// Create a virtual element to position popup from
        const x = p.absolutePosition.left;
        const y = p.absolutePosition.top;
        const virtualElement = {
            getBoundingClientRect: (): DOMRect => {
                return {// Simple dot à (x,y) coords, @see https://popper.js.org/docs/v2/virtual-elements/
                    x,
                    y,
                    width: 0,
                    height: 0,
                    top: y,
                    right: x,
                    bottom: y,
                    left: x,
                    toJSON: () => ""
                };
            }
        };

        popper = createPopper(virtualElement, popupElement, {
            modifiers: [
                preventOverflow,
                offsetModifier,
                flip,
                hide
            ],
            placement: p.popupPosition as Placement ?? PopupPosition.BOTTOM_START
        });
    }

    // Observe for resizes
    const resizeObserver = new ResizeObserver(entries => {
        ignoreResizeObserverLoopLimit(entries, () => popper?.update());
    });

    // Observe for parent resize
    resizeObserver.observe(parent);

    /**
     * Remove all listeners attach to document
     * If there is a callback function defined for the mode (onPopupHide for close /onPopupCancel for cancel) and callCallbackDefined is true we call it
     * Then Remove p.elementToShow and popupElement from DOM
     * @param mode We can close (validation) or cancel the popup
     * @param callCallbackDefined the callback function defined for this mode
     */
    const removePopup = (mode: ShowPopupRemoveMode, callCallbackDefined = true): void => {
        removeAllListeners();
        if (mode === "close") {
            if (callCallbackDefined && p.onPopupHide !== undefined) {
                p.onPopupHide();
            }
        } else if (mode === "cancel") {
            if (callCallbackDefined && p.onPopupCancel !== undefined) {
                p.onPopupCancel();
            }
        }
        popupElement.remove();
        popper?.destroy();
    };

    /**
     * Remove all listeners attach to document
     */
    const removeAllListeners = (): void => {
        document.removeEventListener("mousedown", handleClickWhilePopupIsOpen);
        document.removeEventListener("touchstart", handleClickWhilePopupIsOpen);
        document.removeEventListener(KeyboardEventType.UP, listenerKeyUp);

        resizeObserver.disconnect();
    };

    /**
     * Will check if the Node is in the popup or not
     * @param node the element that we want to check
     */
    const checkNodeIsInPopup = (node: Node | undefined): boolean => {
        return node != null && popupElement.contains(node);
    };

    /**
     * If we click outside of the popup and if this listener is not triggered by the event that call showPopup : we close the popup
     */
    const handleClickWhilePopupIsOpen = (e: MouseEvent | TouchEvent): void => {
        const _pathEvent = e.composedPath();
        const target = _pathEvent[0] as Element | undefined;
        const clickInPopup = checkNodeIsInPopup(target);

        // Check if popup should be closed when user emit a click on app
        const popupCanBeCloseOnOuterBoundsClick = p.popupCanBeCloseOnOuterBoundsClick;
        if (popupCanBeCloseOnOuterBoundsClick != null) {
            if (typeof popupCanBeCloseOnOuterBoundsClick === "function") {
                if (!popupCanBeCloseOnOuterBoundsClick(e)) {
                    return;
                }
            }
            else if (!popupCanBeCloseOnOuterBoundsClick) {
                return;
            }
        }

        if (!clickInPopup && p.event !== e && e.target !== p.event?.target) {
            removePopup(p.popupMode === "direct" ? "cancel" : "close");
        }
    };

    /**
     * Listener on keyup, we want to close or cancel the popup with keyboard
     */
    const listenerKeyUp = (ke: KeyboardEvent): void => {
        if (ke.key === KeyboardInput.ESCAPE && !p.doNotCancelOnEscapeKey) {
            removePopup("cancel");
        } else if (ke.key === KeyboardInput.ENTER && !p.doNotCloseOnEnterKey) {
            removePopup("close");
        }
    };

    // Add event listeners to document
    document.addEventListener("mousedown", handleClickWhilePopupIsOpen);
    document.addEventListener("touchstart", handleClickWhilePopupIsOpen);
    document.addEventListener(KeyboardEventType.UP, listenerKeyUp);

    return {
        popup: popupElement,
        popper,
        removePopup
    };
}

/** Update popper position to avoid open/close */
export function updatePopperPosition(options: InstanceWithNewPopupPosition): void {
    const { newPosition, parent, popper } = options;
    let newReference: VirtualElement | HTMLElement = parent;

    // No new position given, return
    if (!newPosition) {
        return;
    }

    // Change reference if we are in absolute position
    if (newPosition.absolutePosition) {
        const x = newPosition.absolutePosition.left;
        const y = newPosition.absolutePosition.top;

        newReference = {
            getBoundingClientRect: (): DOMRect => ({
                x,
                y,
                width: 0,
                height: 0,
                top: y,
                right: x,
                bottom: y,
                left: x,
                toJSON: () => ""
            })
        };
    }

    // Change reference if needed
    popper.state.elements.reference = newReference;

    // Update position if needed
    popper.state.options.placement = newPosition.popupPosition
        ?? (!newPosition.absolutePosition ? PopupPosition.AUTO : PopupPosition.BOTTOM_START);

    // Force update
    popper.forceUpdate();
}


/**
 * Check if an element is in a modal, check with all his parents
 */
export function isInModal(popupElement: HTMLElement): boolean {
    return getAllParents(popupElement).some(parent => parent.getAttribute("ref") === "dialogModal");
}

/**
 * Handle popup to open in a modal instead of showPopup div
 * Redefine completely the classic showPopup methods
 */
function handlePopupInModal(p: ShowPopupParams): ShowPopupReturn {

    let popupCanBeCloseOnOuterBoundsClickValue = true;

    // Check if popup should be closed when user emit a click on app
    const popupCanBeCloseOnOuterBoundsClick = p.popupCanBeCloseOnOuterBoundsClick;
    if (popupCanBeCloseOnOuterBoundsClick != null) {
        if (typeof popupCanBeCloseOnOuterBoundsClick === "function") {
            if (p.event && !popupCanBeCloseOnOuterBoundsClick(p.event)) {
                popupCanBeCloseOnOuterBoundsClickValue = false;
            }
        }
        else if (!popupCanBeCloseOnOuterBoundsClick) {
            popupCanBeCloseOnOuterBoundsClickValue = false;
        }
    }

    // Create dialog
    const dialog = new CjsDialog({
        backdrop: !popupCanBeCloseOnOuterBoundsClickValue ? "static" : true,
        focus: true,
        enableDismiss: false,
        position: DialogPosition.CENTER,
        hideSections: ["header"],
        contentScrollable: true,
        onConfirmButtonClick: () => {
            p.onPopupHide?.();
            return Promise.resolve();
        },
        onCloseButtonClick: () => {
            p.onPopupCancel?.();
            return Promise.resolve();
        },
        onAfterDialogShow: (dialog: CjsDialog) => {
            const offsetZIndex = 10; // Add to because zIndex difference is 5 between modal classes

            const dialogModal = getElementByRef(dialog, "dialogModal");
            // Update zIndex of modal, to display over previous modal
            dialogModal.style.zIndex = String(BOOTSTRAP_DEFAULT_MODAL_Z_INDEX + offsetZIndex);

            dialogModal.addEventListener("click", (e) => {
                if (!popupCanBeCloseOnOuterBoundsClickValue) { return; }

                // Check if the modal is clicked, not an element inside the modal:
                if (e.target === e.currentTarget) {
                    removeDialog("cancel");
                }
            });

            // Get last backdrop opened in body, update his zIndex
            const backdropsOpened = Array.from(document.querySelectorAll(".modal-backdrop.show")) as HTMLDivElement[];
            const backdrop = backdropsOpened.slice(-1)[0];
            if (backdrop) {
                backdrop.style.zIndex = String(BOOTSTRAP_DEFAULT_BACKDROP_MODAL_Z_INDEX + offsetZIndex);
            }
        },
        ...p.popupInDialogOptions?.dialogOptions
    });

    dialog.addToBody(p.elementToShow);
    dialog.components.dialogBody.classList.add(PADDING.P_0);

    // Use minimum space available for modalDialog content
    const modalDialogDiv = getElementByRef(dialog, "dialogModalDialog");
    modalDialogDiv.style.width = "fit-content";

    // Define remove dialog function
    const removeDialog = (mode: "close" | "cancel", callCallbackDefined = true): void => {

        if (mode === "cancel" && callCallbackDefined && p.onPopupCancel) {
            p.onPopupCancel();
        } else if (mode === "close" && callCallbackDefined && p.onPopupHide) {
            p.onPopupHide();
        }

        // Remove the popupElement
        p.elementToShow.remove();
        dialog.hide();
    };

    return {
        popup: dialog,
        removePopup: removeDialog
    };
}

/**
 * Add css style and classes to an element
 *
 * @param popupElement The element to add css
 * @param [popupCustomCss] The styling to apply
 */
function applyCssStyling(popupElement: HTMLElement, popupCustomCss?: ShowPopupStyling): void {

    // Merge deep popupStyle
    setHtmlElementOptions(popupElement, {
        style: {
            ...defaultCssPopup?.style,
            ...popupCustomCss?.style
        },
        cssClass: [
            ...defaultCssPopup?.cssClass ?? [],
            ...popupCustomCss?.cssClass ?? []
        ],
        attributes: {
            ...defaultCssPopup?.attributes,
            ...popupCustomCss?.attributes
        }
    });
}

/** 
 * @returns the popup container for tooltips / popover, etc.
 * @see https://web.archive.org/web/20210827084020/https://atfzl.com/don-t-attach-tooltips-to-document-body for more explanations
 */
function getDocumentBodyPopupContainer(): HTMLDivElement {
    const ref = "poppersContainer";
    let container = findElementByRef<HTMLDivElement>(document.body, ref);
    if (!container) {// if container isn't already append to document.body, add it
        container = createHtmlElement("div", {
            cssClass: [POSITION.ABSOLUTE],
            attributes: {
                [REF_ATTRIBUTE]: ref
            },
            style: {
                top: toPx(0),
                left: toPx(0)
            }
        });
        document.body.prepend(container);
    }

    return container;
}